import { createLogic } from "redux-logic";
import {
    basketActions,
    basketNotificationActions,
    setConfiguration,
    USC_BASKET_ADD_PACK,
    USC_BASKET_SET_ACCESSORY_IDS,
    USC_BASKET_SET_FINANCE_OPTION,
    USC_BASKET_SET_PACK_IDS,
    type AddAccessoryType,
    type AddPackType,
    type SetAccessoryIdsType,
    type SetFinanceOptionType,
    type SetPackIdsType,
    type UscBasketActionsType,
    type UscBasketNotificationActionsType,
} from "../actions/UscBasketActions";
import { invalidateCode } from "../actions/UscSaveActions";
import { replaceQueryParams } from "../../../../common-deprecated/History";
import type { UscLogicType } from "../store";
import { addNotification } from "../../../../common-deprecated/redux/actions/NotificationActions";
import { startFinanceFetch } from "../actions/UscFinanceActions";
import { setUscBasketConfiguration, uscLocalStorageParamSelector } from "../../utils/localStorage";
import { getUscLabel } from "../../utils/labels";

const financeOptionLogic = (createLogic as UscLogicType<SetFinanceOptionType>)({
    type: USC_BASKET_SET_FINANCE_OPTION,

    process({ action }, dispatch, done) {
        // CCSave keeps track of finance option, invalidate whenever it changes.
        dispatch(invalidateCode());

        // Make sure the current URL reflects the new option as well
        if (!action.disableQueryUpdate) replaceQueryParams({ financeOption: action.option });
        done();
    },
});

const accessoryPackLogic = (createLogic as UscLogicType<UscBasketActionsType>)({
    type: [...basketActions],

    process({ getState }, dispatch, done) {
        const { basket, vehicle } = getState();

        // Trigger a finance fetch to update the monthly payment of the
        // vehicle when an accessory or pack is added or removed.
        dispatch(startFinanceFetch());

        // Update the basket configuration in local storage
        const localStorageParams = uscLocalStorageParamSelector(getState());
        setUscBasketConfiguration(localStorageParams, basket.configuration, vehicle.result?.id || "");

        done();
    },
});

const accessoryPackNotificationLogic = (createLogic as UscLogicType<UscBasketNotificationActionsType>)({
    type: [...basketNotificationActions],
    // Don't run if uscSettings.basketConfig.enableNotification is false
    validate({ getState, action }, allow, reject) {
        const { basketConfig } = getState().uscSettings;
        if (!basketConfig.enableNotifications) {
            reject(action);
        } else {
            allow(action);
        }
    },
    process({ action, getState }, dispatch, done) {
        const state = getState();
        const isPack = [USC_BASKET_ADD_PACK].includes(action.type);
        const name = isPack ? (action as AddPackType).pack.name : (action as AddAccessoryType).accessory.name;

        const notificationDescription = isPack
            ? getUscLabel(state, "uscBasketNotificationPackAdded", { value: name })
            : getUscLabel(state, "uscBasketNotificationAccessoryAdded", { value: name });

        dispatch(
            addNotification([
                {
                    title: "", // Doesn't get used in the notification
                    description: notificationDescription,
                },
            ]),
        );

        done();
    },
});

const basketTyCodeLogic = (createLogic as UscLogicType<SetAccessoryIdsType | SetPackIdsType>)({
    type: [USC_BASKET_SET_ACCESSORY_IDS, USC_BASKET_SET_PACK_IDS],

    process({ action, getState }, dispatch, done) {
        const { basket, accessories } = getState();
        const isPack = action.type === USC_BASKET_SET_PACK_IDS;
        const ids = isPack ? action.packIds : action.accessoryIds;
        const newAccessories = accessories.accessoryResults.filter((accessory) => ids.includes(accessory.id));
        const newPacks = accessories.packResults.filter((pack) => ids.includes(pack.id));

        const newConfiguration = {
            ...basket.configuration,
            accessories: newAccessories,
            packs: newPacks,
        };

        dispatch(setConfiguration(newConfiguration));

        done();
    },
});

export default [financeOptionLogic, accessoryPackLogic, accessoryPackNotificationLogic];

export const basketServerLogic = [basketTyCodeLogic];
